export interface FocusArea {
  name: string;
  title: string;
  description: string;
  symptoms: string[];
  impacts: string[];
  solutions: string[];
  nextSteps: {
    step: string;
    action: string;
  }[];
  insight: string;
}

export const focusAreas: Record<string, FocusArea> = {
  forecasting: {
    name: "Forecasting",
    title: "Predictive Forecasting",
    description:
      "Your biggest opportunity is developing the ability to see what's coming before it happens. Right now, you're making decisions based on what already occurred, not what's likely to happen next. Predictive analytics can help you spot trends early and plan with confidence.",
    symptoms: [
      "Constantly surprised by market changes",
      "Making decisions based on gut feeling",
      "Missing opportunities because you didn't see them coming",
      "Reactive planning instead of proactive strategy",
      "Unclear which data patterns actually predict success",
    ],
    impacts: [
      "Lost revenue from missed market opportunities",
      "Inventory issues - too much or too little at wrong times",
      "Budget surprises and cash flow problems",
      "Competitors moving faster because they see trends first",
      "Team frustration from constant firefighting",
    ],
    solutions: [
      "Trend Analysis Tools: Implement systems that identify patterns in your historical data automatically",
      "Forecasting Models: Build predictive models for key metrics like sales, demand, and customer behavior",
      "Leading Indicators: Identify early warning signals that predict future performance",
      "Scenario Planning: Use analytics to model different future scenarios and their likelihood",
      "Regular Reviews: Create monthly forecast reviews to compare predictions vs. actuals and improve accuracy",
    ],
    nextSteps: [
      {
        step: "Identify Key Metrics",
        action:
          "In 1 week, list the top 3-5 business outcomes you most need to predict (sales, demand, churn, etc.)",
      },
      {
        step: "Gather Historical Data",
        action:
          "Collect at least 12-24 months of historical data for your key metrics",
      },
      {
        step: "Start with Simple Models",
        action:
          "Begin with basic trend analysis and moving averages before advancing to complex algorithms",
      },
      {
        step: "Test and Learn",
        action:
          "Make predictions for next month, track accuracy, and refine your approach based on what works",
      },
    ],
    insight:
      "Prediction isn't about having a crystal ball - it's about recognizing patterns in your data that signal what's likely to happen next. Start simple, measure accuracy, and build confidence in your forecasts over time.",
  },
  dataQuality: {
    name: "Data Quality",
    title: "Data Foundation",
    description:
      "You can't make reliable predictions with unreliable data. Your biggest challenge is cleaning up and organizing your data so it's accurate, complete, and trustworthy. Good analytics starts with good data - and that's where you need to focus first.",
    symptoms: [
      "Data scattered across different systems",
      "Missing information or gaps in records",
      "Inconsistent formats and duplicate entries",
      "Don't trust the numbers enough to make decisions",
      "Spending more time finding/cleaning data than analyzing it",
    ],
    impacts: [
      "Wrong decisions based on bad information",
      "Wasted time reconciling conflicting data sources",
      "Can't build reliable forecasts without clean inputs",
      "Team loses confidence in data-driven approaches",
      "Missed insights hiding in messy, unusable data",
    ],
    solutions: [
      "Data Audit: Map all data sources and identify quality issues, gaps, and inconsistencies",
      "Centralized Database: Create a single source of truth for critical business data",
      "Automated Validation: Implement rules that catch errors at data entry points",
      "Regular Cleansing: Schedule routine data quality checks and cleanup processes",
      "Standards & Governance: Establish clear guidelines for how data should be collected and stored",
    ],
    nextSteps: [
      {
        step: "Data Inventory",
        action:
          "In 2 weeks, document every system where you store important business data",
      },
      {
        step: "Quality Assessment",
        action:
          "Rate each data source on accuracy, completeness, and reliability - identify worst offenders",
      },
      {
        step: "Quick Wins",
        action:
          "Fix the 1-2 biggest data quality issues that would have immediate impact",
      },
      {
        step: "Build Foundation",
        action:
          "Create a master data management plan to prevent future quality problems",
      },
    ],
    insight:
      "Think of data quality as the foundation of a house - you can't build reliable analytics on a shaky base. Invest in getting your data right first, and every prediction you make afterward will be more accurate and trustworthy.",
  },
  actionability: {
    name: "Actionability",
    title: "Actionable Insights",
    description:
      "You might have data and even some insights, but the real challenge is turning those insights into concrete actions that drive results. Predictive analytics is only valuable when it changes what you do - you need better processes to bridge the gap from insight to impact.",
    symptoms: [
      "Reports and dashboards that nobody uses",
      "Insights discussed in meetings but never implemented",
      "Unclear who should act on which insights",
      "Gap between knowing and doing",
      "Analytics feels theoretical instead of practical",
    ],
    impacts: [
      "Wasted investment in tools that don't change behavior",
      "Opportunities identified but not captured",
      "Team cynicism about data initiatives",
      "Competitive disadvantage despite having good information",
      "Slow decision-making while waiting for perfect analysis",
    ],
    solutions: [
      "Action-Oriented Dashboards: Design analytics that highlight specific decisions to make, not just numbers to review",
      "Automated Alerts: Set up notifications when metrics cross thresholds that require action",
      "Clear Ownership: Assign specific people responsible for acting on specific insights",
      "Decision Frameworks: Create playbooks that link predictions to recommended actions",
      "Feedback Loops: Track which actions worked so you learn what to do next time patterns appear",
    ],
    nextSteps: [
      {
        step: "Map Decisions",
        action:
          "In 1 week, list your top 10 recurring business decisions and what data should inform each one",
      },
      {
        step: "Design for Action",
        action:
          "Redesign one key report to clearly show: What happened? What should we do? Who owns it?",
      },
      {
        step: "Start Small",
        action:
          "Pick one predictive insight and create a clear action protocol - test it for a month",
      },
      {
        step: "Measure Impact",
        action:
          "Track business outcomes before and after acting on analytics to prove ROI",
      },
    ],
    insight:
      "The best prediction in the world is worthless if it doesn't change what you do. Focus on making your insights so clear and actionable that the right next step is obvious - then make sure someone owns executing it.",
  },
};
